import { useEffect, useRef } from 'react'
import { Pressable, View } from 'react-native'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context'

import { cn } from '@/utils/cn'
import { useRouter } from '@/utils/navigation'
import { AuthContext, getAuthErrorCode, getAuthErrorStatus, loginWithPassword } from '@/stores/auth'
import { useToast } from '@/components/base'
import { LoginForm, type LoginValues } from '@/components/form'
import { ArrowBack } from '@/components/icon'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import ScreenHeader from '@/components/ScreenHeader'

export default function LoginScreen() {
  const { t } = useTranslation()
  const toast = useToast()
  const auth = AuthContext.useActorRef()
  const router = useRouter()
  const loading = useLoadingIndicator()
  const completingLogin = useRef(false)
  const loggedIn = AuthContext.useSelector((snapshot) => snapshot.matches('LOGGED_IN'))
  const { redirect, params } = useLocalSearchParams<{ redirect: string; params: string }>()

  useEffect(() => {
    if (loggedIn && !completingLogin.current) {
      router.replace('/home')
    }
  }, [loggedIn, router])

  const showLoginError = (error: unknown) => {
    const errorCode = getAuthErrorCode(error)
    const status = getAuthErrorStatus(error)

    if (errorCode === 1026 || status === 429) {
      toast.show({ variant: 'error', description: t('auth:toast.error.loginRateLimited') })
    } else if (
      errorCode === 1007 ||
      (error instanceof Error && (error.message === 'blacklist - no access' || error.message === 'ip_blacklisted'))
    ) {
      toast.show({ variant: 'error', description: t('auth:toast.error.noAccess') })
    } else if (error instanceof Error && error.message === 'Fetch is aborted') {
      toast.show({ variant: 'error', description: t('auth:toast.error.serverError') })
    } else if (error instanceof Error && error.message === 'signal timed out') {
      toast.show({ variant: 'error', description: t('auth:toast.error.timeoutError') })
    } else {
      toast.show({ variant: 'error', description: t('auth:toast.error.loginFailed') })
    }
  }

  const handleLogin = async (data: LoginValues) => {
    loading.start()
    try {
      const authState = await loginWithPassword(data.phoneNumber.code, data.phoneNumber.number, data.password)
      const destination = redirect
        ? {
            pathname: decodeURIComponent(redirect),
            params: params ? JSON.parse(decodeURIComponent(params)) : {}
          }
        : '/home'

      completingLogin.current = true
      auth.send({ type: 'SIGNUP_LOGIN', auth: authState })
      loading.stop()

      // Let the loading overlay leave the native tree before replacing the auth screen.
      // Fabric can otherwise receive overlay removal and stack replacement in one mount transaction.
      requestAnimationFrame(() => router.replace(destination))
    } catch (error) {
      showLoginError(error)
      loading.stop()
    }
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <Header />
        }}
      />

      <SafeAreaView className={'flex-1 bg-background-primary'}>
        <LoginForm onSubmit={handleLogin} />
      </SafeAreaView>
    </>
  )
}

function Header() {
  const router = useRouter()
  const { top } = useSafeAreaInsets()

  return (
    <View className={'bg-background-primary'} style={{ paddingTop: top }}>
      <ScreenHeader
        leftElement={
          <Pressable className={'p-2'} onPress={() => router.back()}>
            {({ pressed }) => <ArrowBack className={cn('size-6 text-text-tertiary', pressed && 'opacity-50')} />}
          </Pressable>
        }
      />
    </View>
  )
}
